import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Client } from '../domain/client/Client';
import { Organization } from '../domain/organization/Organization';
import { Project } from '../domain/project/Project';
import { Stop } from '../domain/stop/Stop';
import { StopFlat } from '../domain/stop/StopFlat';
import { Task } from '../domain/task/Task';
import { clientRepository } from '../repositories/client';
import { ClientRepository } from '../repositories/client/ClientRepository';
import { stopRepository } from '../repositories/stop';
import { CreatePickupData, StopRepository } from '../repositories/stop/StopRepository';
import eventsPanel from '../services/events';

export class StopService {
  constructor(private stopRepository: StopRepository, private clientRepository: ClientRepository) {}

  async fetchStop({ stopId, project }: { stopId: string; project: Project }): Promise<Stop> {
    const stop = await this.stopRepository.fetchStop({ stopId, project });
    return stop;
  }

  async fetchSomeStops({ stopIds, project }: { stopIds: string[]; project: Project }): Promise<Array<Stop | null>> {
    const stops = await this.stopRepository.fetchSomeStops({ stopIds, project });
    return stops;
  }

  async fetchDeliveryByPickup({ pickupId, project }: { pickupId: string; project: Project }): Promise<Stop> {
    const stop = await this.stopRepository.fetchDeliveryByPickup({ pickupId, project });
    return stop;
  }

  async search({
    project,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    project: Project;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Stop>> {
    const plansPagination = await this.stopRepository.search({
      project,
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return plansPagination;
  }

  async fetchFlatStops({
    project,
    predicates,
  }: {
    project: Project;
    predicates?: Array<SearchPredicate>;
  }): Promise<Array<StopFlat>> {
    const flatStops = await this.stopRepository.fetchFlatStops({
      projectId: project.id,
      predicates,
    });

    return flatStops;
  }

  async save({
    stop,
    project,
    organization,
  }: {
    stop: Stop;
    project: Project;
    organization: Organization;
  }): Promise<Stop> {
    const updatedStop = await this.stopRepository.save({ stop, project, organization });
    return updatedStop;
  }

  async saveMany({
    stops,
    project,
    organization,
  }: {
    stops: Stop[];
    project: Project;
    organization: Organization;
  }): Promise<Stop[]> {
    const updatedStops = await this.stopRepository.saveMany({ stops, project, organization });
    return updatedStops;
  }

  async createStops({
    stops,
    planId,
    project,
  }: {
    stops: Array<Stop>;
    planId?: string;
    project: Project;
  }): Promise<Array<Stop>> {
    const createdStops = await this.stopRepository.createStops({ stops, planId, project });

    eventsPanel.createStopsCount(createdStops.length);

    return createdStops;
  }

  async createStopTask({ stop, project }: { stop: Stop; project: Project }): Promise<Stop> {
    const createdTask = await this.stopRepository.createStopTask({ stop, project });

    // Updates the stop array of tasks with the recently updated task
    stop.tasks?.push(createdTask);

    eventsPanel.createStopTask(createdTask);

    return stop;
  }

  async updateStopTask({ stop, task, project }: { stop: Stop; task: Task; project: Project }): Promise<Stop> {
    const createdTask = await this.stopRepository.updateStopTask({ task, project });

    // Updates the stop array of tasks with the recently updated task
    stop.tasks = (stop.tasks ?? []).map((stopTask) => {
      if (stopTask.id === task.id) {
        return createdTask;
      }
      return stopTask;
    });
    return stop;
  }

  async deleteStopTask({ stop, task }: { stop: Stop; task: Task }): Promise<Stop> {
    await this.stopRepository.deleteStopTask({ task });

    // Updates the stop array of tasks with the recently delete task
    stop.tasks = (stop.tasks ?? []).filter((stopTask) => stopTask.id !== task.id);

    return stop;
  }

  async createStopPickup({
    stop,
    pickupData,
    project,
  }: {
    stop: Stop;
    pickupData: CreatePickupData;
    project: Project;
  }): Promise<Stop> {
    const updatedStop = await this.stopRepository.createStopPickup({ stop, pickupData, project });

    if (updatedStop.pickup) {
      stop.addPickup(updatedStop.pickup);
    }

    return stop;
  }

  async deleteStopPickup({ stop }: { stop: Stop }): Promise<Stop> {
    await this.stopRepository.deleteStopPickup({ stop });

    stop.deletePickup();

    return stop;
  }

  async useSaveStopsAsClients({
    stopIds,
    project,
  }: {
    stopIds: Array<string>;
    project: Project;
  }): Promise<Array<Client>> {
    const newClients = await this.clientRepository.createFromStops({ stopIds, project });

    return newClients;
  }

  async moveStopsToPlan({
    stopIds,
    planId,
    project,
  }: {
    stopIds: string[];
    planId?: string;
    project: Project;
  }): Promise<Stop[]> {
    return this.stopRepository.moveStopsToPlan({ stopIds, planId, project });
  }

  async updateConstraints({
    stopIds,
    constraints,
  }: {
    stopIds: string[];
    constraints: Record<string, any>;
  }): Promise<void> {
    return this.stopRepository.updateConstraints(stopIds, constraints);
  }

  async updateSupervisors({ stopIds, supervisors }: { stopIds: string[]; supervisors: Array<string> }): Promise<void> {
    return this.stopRepository.updateSupervisors(stopIds, supervisors);
  }

  async deleteStops({ stopIds, project }: { stopIds: string[]; project: Project }) {
    return this.stopRepository.deleteStops(stopIds, project);
  }
}

export const stopService = new StopService(stopRepository, clientRepository);
